#!/usr/bin/env node

/**
 * @thenoo/cli - CLI scaffolder for @thenoo/agentkit projects
 */

import { Command } from 'commander';
import chalk from 'chalk';
import ora from 'ora';
import prompts from 'prompts';
import validatePackageName from 'validate-npm-package-name';
import { generateProject, getNextSteps } from './generator.js';
import type { ProjectConfig, AdapterType, TemplateType, IDEType } from './types.js';
import { TEMPLATES } from './templates.js';

const program = new Command();

program
  .name('noo')
  .description('CLI scaffolder for @thenoo/agentkit projects')
  .version('0.1.0');

program
  .command('init [name]')
  .description('Initialize a new @thenoo/agentkit project')
  .option('--adapter <type>', 'LLM adapter (openai|azure|none)', 'none')
  .option('--template <type>', 'Project template (minimal|dashboard|serverless)', 'minimal')
  .option('--ide <type>', 'IDE preset (cursor|copilot|vscode|none)', 'none')
  .option('--typescript', 'Use TypeScript (default: true)', true)
  .option('--skip-git', 'Skip git initialization', false)
  .option('--skip-install', 'Skip npm install', false)
  .action(async (name: string | undefined, options) => {
    try {
      console.log(chalk.bold.cyan('\n🚀 @thenoo/agentkit Project Generator\n'));

      // Interactive prompts
      const responses = await prompts(
        [
          {
            type: !name ? 'text' : null,
            name: 'projectName',
            message: 'Project name:',
            initial: 'my-agent',
            validate: (value: string) => {
              const result = validatePackageName(value);
              if (!result.validForNewPackages) {
                return result.errors?.[0] || 'Invalid project name';
              }
              return true;
            },
          },
          {
            type: options.adapter === 'none' ? 'select' : null,
            name: 'adapter',
            message: 'Which adapter would you like to use?',
            choices: [
              { title: 'OpenAI', value: 'openai' },
              { title: 'Azure OpenAI', value: 'azure' },
              { title: 'None (mock only)', value: 'none' },
            ],
            initial: 0,
          },
          {
            type: options.template === 'minimal' ? 'select' : null,
            name: 'template',
            message: 'Which template would you like to use?',
            choices: Object.entries(TEMPLATES).map(([key, meta]) => ({
              title: `${meta.name} - ${meta.description}`,
              value: key,
            })),
            initial: 0,
          },
          {
            type: options.ide === 'none' ? 'select' : null,
            name: 'ide',
            message: 'Which IDE are you using?',
            choices: [
              { title: 'Cursor', value: 'cursor' },
              { title: 'GitHub Copilot', value: 'copilot' },
              { title: 'VS Code', value: 'vscode' },
              { title: 'None', value: 'none' },
            ],
            initial: 0,
          },
        ],
        {
          onCancel: () => {
            console.log(chalk.red('\n❌ Cancelled\n'));
            process.exit(0);
          },
        }
      );

      // Build config
      const config: ProjectConfig = {
        name: name || responses.projectName,
        adapter: (options.adapter !== 'none' ? options.adapter : responses.adapter) as AdapterType,
        template: (options.template !== 'minimal' ? options.template : responses.template) as TemplateType,
        ide: (options.ide !== 'none' ? options.ide : responses.ide) as IDEType,
        typescript: options.typescript,
        skipGit: options.skipGit,
        skipInstall: options.skipInstall,
      };

      // Validate config
      if (!['openai', 'azure', 'none'].includes(config.adapter)) {
        throw new Error(`Invalid adapter: ${config.adapter}`);
      }

      if (!['minimal', 'dashboard', 'serverless'].includes(config.template)) {
        throw new Error(`Invalid template: ${config.template}`);
      }

      if (!['cursor', 'copilot', 'vscode', 'none'].includes(config.ide)) {
        throw new Error(`Invalid IDE: ${config.ide}`);
      }

      // Show configuration
      console.log(chalk.gray('\nConfiguration:'));
      console.log(chalk.gray(`  Name: ${config.name}`));
      console.log(chalk.gray(`  Adapter: ${config.adapter}`));
      console.log(chalk.gray(`  Template: ${config.template}`));
      console.log(chalk.gray(`  IDE: ${config.ide}`));
      console.log(chalk.gray(`  TypeScript: ${config.typescript ? 'Yes' : 'No'}`));
      console.log();

      // Generate project
      const spinner = ora('Creating project...').start();

      try {
        await generateProject(config);
        spinner.succeed(chalk.green('Project created!'));

        // Show next steps
        console.log(getNextSteps(config));
      } catch (error) {
        spinner.fail(chalk.red('Failed to create project'));
        throw error;
      }
    } catch (error) {
      console.error(chalk.red('\n❌ Error:'), (error as Error).message);
      process.exit(1);
    }
  });

program.parse();

